"use client";

import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { persistAppLocaleCookie, type AppLocale } from "@/config/app-locale";

interface AppLocaleContextValue {
  locale: AppLocale;
  setLocale(locale: AppLocale): void;
}

const AppLocaleContext = createContext<AppLocaleContextValue | null>(null);

export function AppLocaleProvider({ children, initialLocale }: { children: React.ReactNode; initialLocale: AppLocale }) {
  const [locale, setLocaleState] = useState(initialLocale);

  const setLocale = useCallback((nextLocale: AppLocale) => {
    setLocaleState(nextLocale);
    persistAppLocaleCookie(nextLocale);
  }, []);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return <AppLocaleContext.Provider value={value}>{children}</AppLocaleContext.Provider>;
}

export function useAppLocale(): AppLocaleContextValue {
  const context = useContext(AppLocaleContext);

  if (context === null) {
    throw new Error("useAppLocale must be used within AppLocaleProvider.");
  }

  return context;
}
